import React, { useMemo } from 'react';
import {
    Autocomplete,
    Box,
    Chip,
    IconButton,
    MenuItem,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight, SkipForward } from 'lucide-react';
import GlassPaper from '../../../components/GlassPaper';
import { TIMEFRAMES } from '../api';
import { loadRecents } from '../recents';
import BacktestPanel from './BacktestPanel';
import FieldLabel from './FieldLabel';

export default function ControlSidebar({
    dates,
    effectiveDate,
    onDateChange,
    symbol,
    symbolInput,
    onSymbolChange,
    onSymbolInputChange,
    symbols,
    symbolsLoading,
    tf,
    onTfChange,
    rth,
    onRthChange,
    isBookless,
    thresholdInput,
    onThresholdInputChange,
    onThresholdCommit,
    slippageInput,
    onSlippageInputChange,
    onSlippageCommit,
    orderQty,
    onOrderQtyChange,
    onRunBacktest,
    backtestRunning,
    runBacktest,
    metrics,
}) {
    const recents = useMemo(() => loadRecents(), [symbol]);

    const dateIndex = dates.indexOf(effectiveDate);
    const hasPrev = dateIndex >= 0 && dateIndex < dates.length - 1;
    const hasNext = dateIndex > 0;

    const goPrev = () => {
        if (hasPrev) onDateChange(dates[dateIndex + 1]);
    };
    const goNext = () => {
        if (hasNext) onDateChange(dates[dateIndex - 1]);
    };
    const goLatest = () => {
        if (dates[0]) onDateChange(dates[0]);
    };

    const symbolOptions = useMemo(() => {
        const merged = [...recents];
        symbols.forEach((s) => {
            if (!merged.includes(s)) merged.push(s);
        });
        return merged;
    }, [recents, symbols]);

    return (
        <Box className="tick-studio__sidebar">
            <GlassPaper sx={{ p: 1.5 }}>
                <FieldLabel label="Session" />
                <Box display="flex" alignItems="center" gap={0.5} mt={0.75} mb={1}>
                    <Tooltip title="Previous trading day (older)">
                        <span>
                            <IconButton size="small" onClick={goPrev} disabled={!hasPrev}>
                                <ChevronLeft size={18} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <TextField
                        select
                        size="small"
                        label="Date"
                        value={effectiveDate}
                        onChange={(e) => onDateChange(e.target.value)}
                        sx={{ flex: 1, minWidth: 0 }}
                    >
                        {dates.map((d) => (
                            <MenuItem key={d} value={d}>
                                {d}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Tooltip title="Next trading day (newer)">
                        <span>
                            <IconButton size="small" onClick={goNext} disabled={!hasNext}>
                                <ChevronRight size={18} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Jump to latest">
                        <span>
                            <IconButton size="small" onClick={goLatest} disabled={!dates[0] || effectiveDate === dates[0]}>
                                <SkipForward size={16} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>

                <Autocomplete
                    size="small"
                    fullWidth
                    options={symbolOptions}
                    value={symbol}
                    onChange={(_, v) => onSymbolChange(v)}
                    inputValue={symbolInput}
                    onInputChange={(_, v) => onSymbolInputChange(v)}
                    loading={symbolsLoading}
                    filterOptions={(opts) => opts}
                    noOptionsText={
                        symbolInput.trim() ? 'No matching instruments' : 'Type to search instruments'
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Symbol"
                            placeholder="type to search…"
                            helperText="e.g. NIFTY, RELIANCE, BANKNIFTY26JUNFUT"
                        />
                    )}
                />

                {recents.length > 0 && (
                    <Box display="flex" gap={0.5} flexWrap="wrap" mt={1}>
                        <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                            Recents
                        </Typography>
                        {recents.slice(0, 5).map((s) => (
                            <Chip
                                key={s}
                                label={s}
                                size="small"
                                variant={symbol === s ? 'filled' : 'outlined'}
                                onClick={() => onSymbolChange(s)}
                                sx={{ fontSize: 10, maxWidth: '100%' }}
                            />
                        ))}
                    </Box>
                )}

                <Box mt={1.5}>
                    <FieldLabel label="Timeframe" />
                    <ToggleButtonGroup
                        value={tf}
                        exclusive
                        size="small"
                        fullWidth
                        onChange={(_, v) => v && onTfChange(v)}
                        sx={{ mt: 0.5 }}
                    >
                        {TIMEFRAMES.map((t) => (
                            <ToggleButton key={t} value={t} sx={{ flex: 1, py: 0.4, fontSize: 11 }}>
                                {t}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>

                <Box display="flex" alignItems="center" justifyContent="space-between" mt={1.5}>
                    <Typography variant="caption">RTH only (09:15–15:30)</Typography>
                    <ToggleButton
                        value="rth"
                        selected={rth}
                        size="small"
                        onChange={() => onRthChange(!rth)}
                        sx={{ px: 1.5, py: 0.25, fontSize: 11 }}
                    >
                        {rth ? 'On' : 'Off'}
                    </ToggleButton>
                </Box>
            </GlassPaper>

            <BacktestPanel
                isBookless={isBookless}
                thresholdInput={thresholdInput}
                onThresholdInputChange={onThresholdInputChange}
                onThresholdCommit={onThresholdCommit}
                slippageInput={slippageInput}
                onSlippageInputChange={onSlippageInputChange}
                onSlippageCommit={onSlippageCommit}
                orderQty={orderQty}
                onOrderQtyChange={onOrderQtyChange}
                onRunBacktest={onRunBacktest}
                backtestRunning={backtestRunning}
                runBacktest={runBacktest}
                metrics={metrics}
            />
        </Box>
    );
}
